import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import Text from 'src/components/ts-components/Text'
import CopyComponent from 'src/components/CopyComponent'

const PLACEHOLDERS = {
  player: '/images/generic_placeholders/placeholder_player.png',
  team: '/images/generic_placeholders/placeholder_team.png',
} as const

const LABELS = {
  player: {
    caption: 'Player Name',
    idLabel: 'Player ID',
    fallbackName: 'View player',
  },
  team: {
    caption: 'Team Name',
    idLabel: 'Team ID',
    fallbackName: 'View team',
  },
} as const

type EntityKind = 'player' | 'team'

interface EntityProfileLinkProps {
  kind: EntityKind
  id: string
  name?: string | null
  image?: { full_path?: string; filename?: string } | string | null
  /** When true, name renders as plain text instead of a link (e.g. bucket teams). */
  disableLink?: boolean
}

const EntityProfileLink = ({
  kind,
  id,
  name,
  image,
  disableLink = false,
}: EntityProfileLinkProps) => {
  const { caption, idLabel, fallbackName } = LABELS[kind]
  const placeholder = PLACEHOLDERS[kind]
  const displayName = name || fallbackName

  const imageSrc = getImageThumbnailId({ image }) || placeholder
  const href =
    kind === 'player'
      ? `${ROUTES.PLAYER_ROOT}/${id}`
      : `${ROUTES.TEAM_ROOT}/${id}`

  const nameContent = (
    <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
      {displayName}
    </Text>
  )

  return (
    <Container>
      <IdSlot>
        <CopyComponent
          customText={idLabel}
          id={id}
          alternativeBackground={false}
        />
      </IdSlot>
      <Avatar
        src={imageSrc}
        alt={displayName}
        width={80}
        height={80}
        isCircular={kind === 'player'}
        onError={(e) => {
          const img = e.target as HTMLImageElement
          img.onerror = null
          img.src = placeholder
        }}
      />
      <Info>
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {caption}
        </Text>
        {disableLink ? (
          nameContent
        ) : (
          <NameLink href={href} target='_blank' rel='noopener noreferrer'>
            {nameContent}
          </NameLink>
        )}
      </Info>
    </Container>
  )
}

export default EntityProfileLink

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: ${colors.SECONDARY_200};
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
`

const IdSlot = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`

const Avatar = styled.img<{ isCircular?: boolean }>`
  border-radius: ${({ isCircular }) => (isCircular ? '50%' : '8px')};
  object-fit: contain;
  background: ${colors.NEUTRAL_0};
  flex-shrink: 0;
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const NameLink = styled.a`
  color: ${colors.WHITE};
  text-decoration: none;
  &:hover {
    color: ${colors.WHITE};
    text-decoration: underline;
  }
`
